import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Save, FileText, RefreshCw, Plus, Trash2, ChevronDown, ChevronUp, Shield, Scale, Cookie } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminLegalPagesPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy');
  const [pages, setPages] = useState({
    privacy: { title: '', last_updated: '', sections: [] },
    terms: { title: '', last_updated: '', sections: [] },
    cookies: { title: '', last_updated: '', sections: [] }
  });
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    loadAllPages();
  }, []);

  const loadAllPages = async () => {
    setLoading(true);
    try {
      const [privacyRes, termsRes, cookiesRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/legal/privacy`),
        fetch(`${API_URL}/api/cms/legal/terms`),
        fetch(`${API_URL}/api/cms/legal/cookies`)
      ]);
      
      const [privacy, terms, cookies] = await Promise.all([
        privacyRes.json(),
        termsRes.json(),
        cookiesRes.json()
      ]);
      
      setPages({ privacy, terms, cookies });
    } catch (error) {
      console.error('Failed to load legal pages:', error);
      toast.error('Failed to load legal pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (pageType) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/cms/legal/${pageType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(pages[pageType])
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success(`${pages[pageType].title} saved successfully`);
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (pageType, field, value) => {
    setPages(prev => ({
      ...prev,
      [pageType]: { ...prev[pageType], [field]: value }
    }));
  };

  const handleSectionChange = (pageType, index, field, value) => {
    const newSections = [...pages[pageType].sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setPages(prev => ({
      ...prev,
      [pageType]: { ...prev[pageType], sections: newSections }
    }));
  };

  const addSection = (pageType) => {
    setPages(prev => ({
      ...prev,
      [pageType]: {
        ...prev[pageType],
        sections: [...prev[pageType].sections, { title: 'New Section', content: '' }]
      }
    }));
  };

  const removeSection = (pageType, index) => {
    setPages(prev => ({
      ...prev,
      [pageType]: {
        ...prev[pageType],
        sections: prev[pageType].sections.filter((_, i) => i !== index)
      }
    }));
  };

  const toggleSection = (pageType, index) => {
    const key = `${pageType}-${index}`;
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const tabs = [
    { id: 'privacy', label: 'Privacy Policy', icon: Shield },
    { id: 'terms', label: 'Terms of Service', icon: Scale },
    { id: 'cookies', label: 'Cookie Policy', icon: Cookie }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-tech-blue" />
      </div>
    );
  }

  const currentPage = pages[activeTab];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-navy">Legal Pages</h1>
          <p className="text-gray-500">Manage Privacy Policy, Terms of Service, and Cookie Policy</p>
        </div>
        <Button 
          onClick={() => handleSave(activeTab)} 
          disabled={saving} 
          className="bg-tech-blue hover:bg-tech-blue/90"
        >
          {saving ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
          Save {tabs.find(t => t.id === activeTab)?.label}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-tech-blue text-tech-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Page Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-tech-blue" />
            Page Settings
          </CardTitle>
          <CardDescription>Configure the page title and last updated date</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Page Title</label>
              <Input
                value={currentPage.title}
                onChange={(e) => handlePageChange(activeTab, 'title', e.target.value)}
                placeholder="Privacy Policy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Last Updated</label>
              <Input
                value={currentPage.last_updated}
                onChange={(e) => handlePageChange(activeTab, 'last_updated', e.target.value)}
                placeholder="February 2026"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Content Sections</span>
            <Button size="sm" onClick={() => addSection(activeTab)}>
              <Plus className="h-4 w-4 mr-2" /> Add Section
            </Button>
          </CardTitle>
          <CardDescription>Manage the content sections of this page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentPage.sections?.map((section, index) => {
            const isExpanded = expandedSections[`${activeTab}-${index}`];
            return (
              <div key={index} className="border rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer"
                  onClick={() => toggleSection(activeTab, index)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <span className="font-medium">{section.title || `Section ${index + 1}`}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSection(activeTab, index);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                {isExpanded && (
                  <div className="p-4 space-y-4 border-t">
                    <div>
                      <label className="block text-sm font-medium mb-1">Section Title</label>
                      <Input
                        value={section.title}
                        onChange={(e) => handleSectionChange(activeTab, index, 'title', e.target.value)}
                        placeholder="Section Title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Content</label>
                      <textarea
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-tech-blue/20 min-h-[150px]"
                        value={section.content}
                        onChange={(e) => handleSectionChange(activeTab, index, 'content', e.target.value)}
                        placeholder="Enter section content..."
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {currentPage.sections?.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No sections yet. Click "Add Section" to create content.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Link */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Preview this page:</span>
            <a 
              href={`/${activeTab === 'privacy' ? 'privacy-policy' : activeTab === 'terms' ? 'terms-of-service' : 'cookie-policy'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-tech-blue hover:underline text-sm"
            >
              Open in new tab →
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLegalPagesPage;
